import { z } from 'zod';
import { ColumnMappingSchema, NamedMappingSchema } from '../imports/mapping.js';

/** Worker API bodies and answers for CSV imports and intake sources (M8). */

export const PERSON_CONSENT_SOURCES = ['web_form', 'vendor', 'verbal', 'written', 'other'] as const;
export type PersonConsentSource = (typeof PERSON_CONSENT_SOURCES)[number];

export const PERSON_CONSENT_SOURCE_LABELS: Record<PersonConsentSource, string> = {
  web_form: 'Web form',
  vendor: 'Lead vendor',
  verbal: 'Verbal (call or meeting)',
  written: 'Written (signed form, email)',
  other: 'Other',
};

/**
 * The attestation an admin ticks before an import runs (owner decisions 2026-10-14 #1 and #6), word for
 * word as shown and as recorded in audit_log.
 */
export function importAttestation(workspaceName: string): string {
  return `Every contact in this file agreed to receive texts from ${workspaceName}, and the file was checked against any do-not-call lists that apply.`;
}

/** The same promise for a webhook source that does not send a consent time with each lead (#24). */
export function intakeAttestation(workspaceName: string): string {
  return `Every lead this source sends agreed to receive texts from ${workspaceName} before it is sent, and was checked against any do-not-call lists that apply.`;
}

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose a date');

export const AssignmentSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('single'),
    userId: z.uuid({ error: 'Choose who gets these merchants' }),
  }),
  z.object({
    mode: z.literal('round_robin'),
    userIds: z
      .array(z.uuid())
      .min(1, 'Choose at least one person for round robin')
      .max(100)
      .refine((ids) => new Set(ids).size === ids.length, 'Each person once'),
  }),
]);
export type Assignment = z.infer<typeof AssignmentSchema>;

/** The admin's consent statement for a file (owner decision 2026-10-14 #1). */
export const ImportConsentSchema = z.object({
  source: z.enum(PERSON_CONSENT_SOURCES, { error: 'Choose where consent came from' }),
  sourceDetail: z.string().trim().max(300).default(''),
  scope: z.string().trim().min(1, 'Say what the consent covers').max(500),
  onOrBefore: IsoDate,
  attested: z.literal(true, { error: 'Confirm that every contact in this file agreed to texts' }),
});
export type ImportConsent = z.infer<typeof ImportConsentSchema>;

export const ImportSettingsSchema = z.object({
  mapping: ColumnMappingSchema,
  /** The vendor (CSV intake source) this file comes from, if any. */
  sourceId: z.uuid().nullable().default(null),
  /** Save this mapping for the vendor: a new source with this name, or the chosen source. */
  saveMappingAs: z.string().trim().min(1).max(80).nullable().default(null),
  assignment: AssignmentSchema,
  defaultTagIds: z.array(z.uuid()).max(20).default([]),
  /** Add the automatic "Import <date> – <file>" tag (owner decision #34; can be unticked). */
  batchTag: z.boolean().default(true),
  existingMode: z.enum(['skip', 'fill_blanks']).default('skip'),
  lookup: z
    .object({
      enabled: z.boolean(),
      /** The cost the admin saw and accepted, in cents; the run stops before spending more. */
      maxCents: z
        .string()
        .regex(/^\d{1,8}(\.\d{1,4})?$/)
        .nullable()
        .default(null),
    })
    .default({ enabled: false, maxCents: null }),
});
export type ImportSettings = z.infer<typeof ImportSettingsSchema>;

export const CheckImportRequestSchema = ImportSettingsSchema.extend({
  /** Optional on a check: when given with a consent-date column, it is used as the reference date. */
  consent: ImportConsentSchema.partial().optional(),
});
export type CheckImportRequest = z.infer<typeof CheckImportRequestSchema>;

export const StartImportRequestSchema = ImportSettingsSchema.extend({
  consent: ImportConsentSchema,
});
export type StartImportRequest = z.infer<typeof StartImportRequestSchema>;

export const ImportPreviewSchema = z.object({
  importId: z.uuid(),
  fileName: z.string(),
  encoding: z.string(),
  delimiter: z.string(),
  columns: z.array(z.string()),
  /** The first rows, as decoded (so a wrong encoding shows before anything is imported). */
  sample: z.array(z.array(z.string())),
  totalRows: z.number().int(),
  suggestedMapping: ColumnMappingSchema,
  sourceId: z.uuid().nullable(),
  status: z.string(),
});
export type ImportPreview = z.infer<typeof ImportPreviewSchema>;

export const IMPORT_OUTCOMES = [
  'created',
  'updated',
  'skipped_duplicate',
  'skipped_existing',
  'skipped_archived',
  'skipped_landline',
  'skipped_invalid',
  'skipped_outside_us',
  'skipped_suppressed',
  'skipped_vendor_dnc',
  'skipped_no_consent',
] as const;
export type ImportOutcome = (typeof IMPORT_OUTCOMES)[number];

export const IMPORT_OUTCOME_LABELS: Record<ImportOutcome, string> = {
  created: 'New merchants',
  updated: 'Existing merchants filled in',
  skipped_duplicate: 'Same number twice in the file',
  skipped_existing: 'Already in your workspace',
  skipped_archived: 'Archived merchants',
  skipped_landline: 'Landlines',
  skipped_invalid: 'Invalid numbers',
  skipped_outside_us: 'Outside the US',
  skipped_suppressed: 'On the suppression list',
  skipped_vendor_dnc: 'Marked do not contact by the vendor',
  skipped_no_consent: 'No consent date',
};

export const ImportCountsSchema = z.object(
  Object.fromEntries(IMPORT_OUTCOMES.map((o) => [o, z.number().int()])) as Record<
    ImportOutcome,
    z.ZodNumber
  >,
);
export type ImportCounts = z.infer<typeof ImportCountsSchema>;

export const ImportCheckSchema = z.object({
  totalRows: z.number().int(),
  counts: ImportCountsSchema,
  needsState: z.number().int(),
  warnings: z.number().int(),
  /** New numbers a carrier lookup would pay for (not in the workspace's cache). */
  lookupsNeeded: z.number().int(),
  lookupCostCents: z.string(),
  issues: z.array(
    z.object({ row: z.number().int(), outcome: z.string(), reason: z.string().nullable() }),
  ),
});
export type ImportCheck = z.infer<typeof ImportCheckSchema>;

export const StartImportResponseSchema = z.object({ importId: z.uuid(), status: z.string() });
export const ArchiveImportResponseSchema = z.object({ archived: z.number().int() });

// ─────────────────────────── Intake sources (webhook lead intake) ───────────────────────────

export const IntakeSourceRequestSchema = z.object({
  name: z.string().trim().min(1, 'Name the source').max(80),
  mapping: NamedMappingSchema.default({}),
  assignment: AssignmentSchema,
  defaultTagIds: z.array(z.uuid()).max(20).default([]),
  consentSource: z.enum(PERSON_CONSENT_SOURCES, { error: 'Choose where consent comes from' }),
  consentScope: z.string().trim().min(1, 'Say what the consent covers').max(500),
  /** Owner decision #24: on by default; off only with the attestation below. */
  requireConsentTimestamp: z.boolean().default(true),
  attested: z.boolean().default(false),
  lookupEnabled: z.boolean().default(false),
});
export type IntakeSourceRequest = z.infer<typeof IntakeSourceRequestSchema>;

export const IntakeSourceResponseSchema = z.object({
  id: z.uuid(),
  /** Shown once (on create and on rotate); only its hash is stored. */
  apiKey: z.string().nullable(),
});
export type IntakeSourceResponse = z.infer<typeof IntakeSourceResponseSchema>;

export const OkResponseSchema = z.object({ ok: z.literal(true) });
